// src/components/Warehouse/WarehouseItemUpload.js

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import { useCompany } from "context/CompanyContext";
import { useSnackbar } from "context/SnackbarContext";
import api from "services/api";
import { Box, Modal, Typography, TextField, IconButton } from "@mui/material";
import { Close, Add as AddIcon } from "@mui/icons-material";
import { useLanguage } from "context/LanguageContext";

// Helper function to get CSRF token from cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const WarehouseItemUpload = ({ onUploadSuccess, children }) => {
  const [open, setOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { t } = useLanguage();
  const { selectedCompany } = useCompany();
  const showSnackbar = useSnackbar();
  const [csrfToken, setCsrfToken] = useState("");

  useEffect(() => {
    // Fetch CSRF token from the backend
    const fetchCsrfToken = async () => {
      try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, {
          method: "GET",
          credentials: "include", // Include cookies
        });
        const token = getCookie("csrftoken");
        setCsrfToken(token);
      } catch (error) {
        console.error("CSRF token fetch error:", error);
        showSnackbar("Failed to fetch CSRF token.", "error");
      }
    };

    fetchCsrfToken();
  }, [showSnackbar]);

  const handleFileChange = (event) => {
    setSelectedFiles(event.target.files);
  };

  const handleUpload = async () => {
    if (!selectedCompany) {
      showSnackbar("Please select a company before uploading warehouse items.", "error");
      return;
    }
    if (selectedFiles.length === 0) {
      showSnackbar(t("selectAtLeastOneFile"), "error");
      return;
    }

    const formData = new FormData();
    formData.append("company_id", selectedCompany.id);
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append("file", selectedFiles[i]);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/warehouse/upload/`, {
        method: "POST",
        body: formData,
        credentials: "include", // Include cookies
        headers: {
          "X-CSRFToken": csrfToken, // Include CSRF token
        },
      });
      let result;
      try {
        result = await response.json();
      } catch (e) {
        // Fallback to text if not JSON
        const text = await response.text();
        throw new Error(text || "Server returned a non-JSON response");
      }

      if (response.ok && result.success) {
        showSnackbar("Warehouse items uploaded successfully.", "success");
        onUploadSuccess();
        setSelectedFiles([]);
        setOpen(false);
      } else {
        const msg =
          result.error || (Array.isArray(result.errors) && result.errors[0]) || "Upload failed";
        showSnackbar(`Error: ${msg}`, "error");
      }
    } catch (error) {
      console.error("Error uploading warehouse items:", error);
      showSnackbar(
        error.message ||
          t("failedToUploadWarehouseItems") ||
          "Upload failed due to network/server error.",
        "error"
      );
    }
  };

  return (
    <>
      <MDButton
        variant="contained"
        color="info"
        onClick={() => setOpen(true)}
        startIcon={<AddIcon />}
      >
        {children || "Upload Items"}
      </MDButton>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="upload-modal-title"
        aria-describedby="upload-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography id="upload-modal-title" variant="h6" component="h2">
              {t("uploadItems")}
            </Typography>
            <IconButton edge="end" aria-label="close" onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>

          <TextField
            type="file"
            inputProps={{ multiple: true }}
            onChange={handleFileChange}
            fullWidth
            sx={{ mb: 2 }}
          />

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="outlined" color="info" onClick={() => setOpen(false)}>
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="info"
              onClick={handleUpload}
              disabled={selectedFiles.length === 0}
            >
              Upload
            </MDButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

WarehouseItemUpload.propTypes = {
  onUploadSuccess: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default WarehouseItemUpload;
